import { Card, Avatar, Image, Upload, Button, message, Dropdown } from 'antd';
import { CameraOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';

import useEgresado from './../../../hooks/HookEgresado';
import PersonalInfo from "./InfPersonal";
import { useState } from 'react';

// Hook personalizado para validación de archivos
const useFileValidation = (maxSize = 2) => {
  return (file) => {
    const isValidType = file.type === 'image/jpeg' || file.type === 'image/png';
    const isValidSize = file.size / 1024 / 1024 < maxSize;

    if (!isValidType) {
      message.error('Solo se permiten archivos JPG o PNG.');
      return false;
    }

    if (!isValidSize) {
      message.error(`El tamaño del archivo no debe exceder los ${maxSize}MB.`);
      return false;
    }

    return true;
  };
};

// Componente reutilizable para el menú de opciones
const ImageActionDropdown = ({ type, onUpload, onDelete, buttonStyle, uploadLabel }) => {
  const validateFile = useFileValidation(2);

  const menuItems = [
    {
      key: 'update',
      icon: type === 'PERFIL' ? <CameraOutlined /> : <EditOutlined />,
      label: (
        <Upload
          beforeUpload={(file) => {
            if (validateFile(file)) {
              onUpload(file, type);
            }
            return false;
          }}
          showUploadList={false}
          accept=".jpg,.jpeg,.png"
        >
          <span>{uploadLabel}</span>
        </Upload>
      ),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: type === 'PERFIL' ? 'Eliminar foto' : 'Eliminar portada',
      danger: true,
      onClick: () => onDelete(type),
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['hover']}
      placement="bottomRight"
    >
      <Button
        shape="circle"
        type="ghost"
        icon={<SettingOutlined />}
        style={buttonStyle}
      />
    </Dropdown>
  );
};

// Componente optimizado para el avatar

const AvatarUpload = ({ src, onUpload, onDelete, loading }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div style={{ position: 'relative', width: '120px', maxWidth: '20vw' }}>
      {/* Skeleton loader con animación shimmer para avatar circular */}
      {(!imageLoaded || loading) && (
        <div 
          style={{
            position: 'absolute',
            width: '100%',
            aspectRatio: '1 / 1',
            borderRadius: '50%',
            border: '3px solid white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            backgroundColor: '#f0f0f0',
            backgroundImage: `
              linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.4),
                transparent
              )
            `,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            zIndex: 1,
          }}
        />
      )}

      <Avatar
        src={src}
        style={{
          width: '100%',
          height: 'auto',
          aspectRatio: '1 / 1',
          border: '3px solid white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          borderRadius: '50%',
          transition: 'opacity 0.5s ease-in-out',
          opacity: imageLoaded && !loading ? 1 : 0,
        }}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(true)} // También termina loading en error
      />

      <ImageActionDropdown
        type="PERFIL"
        onUpload={onUpload}
        onDelete={onDelete}
        uploadLabel="Cambiar foto"
        buttonStyle={{
          position: 'absolute',
          bottom: '8px',
          right: '-12px',
          border: '2px solid white',
          background: '#1890ff',
          color: 'white',
          width: '32px',
          height: '32px',
        }}
      />

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};


// Componente optimizado para la portada

const CoverUpload = ({ src, onUpload, onDelete, loading }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: '#56212f',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: 'auto',
        minHeight: 200,
        maxHeight: 300,
      }}
    >
      {/* Skeleton loader con animación de pulso */}
      {(!imageLoaded || loading) && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: '#56212f',
            backgroundImage: `
              linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.1),
                transparent
              )
            `,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            zIndex: 1,
          }}
        />
      )}

      <Image
        alt="Portada del egresado"
        src={src}
        style={{
          width: '100%',
          height: 'auto',
          minHeight: 200,
          maxHeight: 300,
          objectFit: 'contain',
          transition: 'opacity 0.5s ease-in-out',
          opacity: imageLoaded && !loading ? 1 : 0,
        }}
        preview={false}
        onLoad={() => setImageLoaded(true)}
      />

      <ImageActionDropdown
        type="PORTADA"
        onUpload={onUpload}
        onDelete={onDelete}
        uploadLabel="Actualizar portada"
        buttonStyle={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          borderRadius: '50%',
          opacity: 0.85,
          background: 'rgba(0,0,0,0.5)',
          border: 'none',
          color: 'white',
        }}
      />

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};

// Componente principal optimizado
const Profile = ({ id }) => {
  const { loading, handleUpload, handleDelete, imageProfile, imagePortada } = useEgresado();
  return (
    <Card
      id={id}
      style={{ borderRadius: 12, overflow: 'hidden' }}
      cover={
        <div style={{ position: 'relative' }}>
          <CoverUpload
            src={imagePortada}
            onUpload={handleUpload}
            onDelete={handleDelete}
            loading={loading}
          />
          <div style={{
            position: 'absolute',
            bottom: '-32px',
            left: '16px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <AvatarUpload
              src={imageProfile}
              onUpload={handleUpload}
              onDelete={handleDelete}
              loading={loading}
            />
          </div>
        </div>
      }
    >
      <PersonalInfo />
    </Card>
  );
};

export default Profile;